// LiveTranscript (calls round): who said what on the call, as they say it, beside the booking. Each line
// is its speaker by name (the caller once matched, you by yours, a third person a transfer brings in),
// when, and the words. Where a suggestion came from is underlined in the words: dashed while it waits,
// solid once used, shaded while its field's Show where is on (DESIGN.md §9.6). Any line can be copied
// into a field (Use this line…, a menu of the fields that take words). Events (Lou joined) sit between
// lines, muted. Its head says how it stands: starting, live, paused on hold, no transcript (the booking
// works as usual), done, or pending: no transcript yet (none has come, or transcripts aren't on yet).
// New lines are read out politely while it's live. What each side is still saying (partial) follows the
// lines, muted over a dashed rule, and isn't read out: its final takes its place. Reads only --transcript-*.
import { useRef, useState, type ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Menu } from '../Menu/Menu.tsx';
import './LiveTranscript.css';

export type TranscriptStatus = 'starting' | 'live' | 'held' | 'unavailable' | 'done' | 'pending';

export interface TranscriptLine {
  /** The speaker's name as shown ("Ana", "Marguerite", "Lou"). */
  who: string;
  /** The signed-in person: their lines are set apart. */
  isMe?: boolean;
  /** When on the call ("0:16"). */
  at: string;
  text: string;
  /** Not words but something that happened (Lou joined the call). */
  isEvent?: boolean;
}

/** Where a suggestion came from: a phrase in one line. */
export interface TranscriptMark {
  line: number;
  phrase: string;
  /** The field it's for (the key the booking uses). */
  field: string;
  isUsed?: boolean;
  isShown?: boolean;
}

export interface CopyTarget {
  id: string;
  label: string;
}

export interface LiveTranscriptProps {
  status: TranscriptStatus;
  lines: TranscriptLine[];
  /** What's still being said, at most one a side: replaced as it's said, then by its final line. */
  partial?: TranscriptLine[];
  /** Who's on it, after the state ("Ana, Marguerite"); when done, how long and who. */
  people?: string;
  /** A line under the state ("Marguerite hears “Please hold, we’ll be right with you.”"). */
  note?: string;
  marks?: TranscriptMark[];
  /** The fields a line can be copied into; none, and lines can't be copied. */
  copyTargets?: CopyTarget[];
  onCopy?: (line: number, field: string) => void;
  /** A line just copied, and the field's name: said under it. */
  copied?: { line: number; into: string };
  /** Under the lines ("Suggestions wait beside each field: nothing goes in the booking until you use it."). */
  foot?: ReactNode;
}

const HEADS: Record<TranscriptStatus, string> = {
  starting: 'Transcript starting',
  live: 'Live',
  held: 'Paused: on hold',
  unavailable: 'No transcript for this call',
  done: 'Transcript',
  pending: 'No transcript yet',
};

/** A line's words with its marks underlined, in the order they appear. */
function marked(text: string, marks: TranscriptMark[]): ReactNode {
  const found = marks
    .map((m) => ({ m, at: text.indexOf(m.phrase) }))
    .filter((x) => x.at >= 0)
    .sort((a, b) => a.at - b.at);
  const out: ReactNode[] = [];
  let i = 0;
  for (const { m, at } of found) {
    if (at < i) continue;
    if (at > i) out.push(text.slice(i, at));
    out.push(
      <mark
        key={`${m.field}-${at}`}
        className="fl-transcript__mark"
        data-used={m.isUsed ? '' : undefined}
        data-shown={m.isShown ? '' : undefined}
      >
        {m.phrase}
      </mark>,
    );
    i = at + m.phrase.length;
  }
  if (i < text.length) out.push(text.slice(i));
  return out;
}

function UseLine({
  index,
  targets,
  onCopy,
}: {
  index: number;
  targets: CopyTarget[];
  onCopy?: (line: number, field: string) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <span className="fl-transcript__use">
      <AriaButton
        ref={ref}
        className="fl-transcript__use-b"
        aria-haspopup="menu"
        aria-expanded={open}
        onPress={() => setOpen(!open)}
      >
        Use this line…
      </AriaButton>
      <Menu
        at={open && ref.current ? { element: ref.current } : null}
        onClose={() => setOpen(false)}
        label="Copy this line into"
        title="Copy this line into"
        sections={[{ items: targets.map((t) => ({ id: t.id, label: t.label })) }]}
        onAction={(id) => {
          setOpen(false);
          onCopy?.(index, id);
        }}
      />
    </span>
  );
}

export function LiveTranscript({
  status,
  lines,
  partial = [],
  people,
  note,
  marks = [],
  copyTargets = [],
  onCopy,
  copied,
  foot,
}: LiveTranscriptProps) {
  const markOf =
    status === 'starting' || status === 'held' || status === 'pending'
      ? 'wait'
      : status === 'live'
        ? 'now'
        : status;
  const canCopy =
    copyTargets.length > 0 && status !== 'done' && status !== 'unavailable' && status !== 'pending';
  return (
    <div className="fl-transcript" data-status={status}>
      <p className="fl-transcript__state">
        <span className="fl-transcript__smark" data-mark={markOf} aria-hidden="true" />
        <b>{HEADS[status]}</b>
        {people ? <span className="fl-transcript__people">{people}</span> : null}
      </p>
      {note ? <p className="fl-transcript__note">{note}</p> : null}
      {status === 'unavailable' ? (
        <p className="fl-transcript__empty">
          The booking works as usual: fill it in as you talk. What they say that the technician needs goes in
          Notes for the technician.
        </p>
      ) : status === 'pending' && !lines.length && !partial.length ? (
        <p className="fl-transcript__empty">What’s said shows here as they talk.</p>
      ) : (
        <ol className="fl-transcript__lines" aria-live={status === 'live' ? 'polite' : 'off'}>
          {lines.map((l, i) =>
            l.isEvent ? (
              <li key={i} className="fl-transcript__event">
                <span className="fl-transcript__at">{l.at}</span>
                <span>{l.text}</span>
              </li>
            ) : (
              <li key={i} className="fl-transcript__line" data-me={l.isMe ? '' : undefined}>
                <span className="fl-transcript__who">{l.who}</span>
                <span className="fl-transcript__at">{l.at}</span>
                <p className="fl-transcript__words">
                  {marked(
                    l.text,
                    marks.filter((m) => m.line === i),
                  )}
                </p>
                {copied?.line === i ? (
                  <span className="fl-transcript__copied">Copied into {copied.into}</span>
                ) : null}
                {canCopy ? <UseLine index={i} targets={copyTargets} onCopy={onCopy} /> : null}
              </li>
            ),
          )}
        </ol>
      )}
      {partial.length && status !== 'unavailable' ? (
        <ol className="fl-transcript__lines" aria-label="Still being said" aria-live="off">
          {partial.map((l, i) => (
            <li key={i} className="fl-transcript__line" data-me={l.isMe ? '' : undefined} data-partial="">
              <span className="fl-transcript__who">{l.who}</span>
              <span className="fl-transcript__at">{l.at}</span>
              <p className="fl-transcript__words">{l.text}</p>
            </li>
          ))}
        </ol>
      ) : null}
      {foot ? <p className="fl-transcript__foot">{foot}</p> : null}
    </div>
  );
}
